import { useMemo, useCallback } from 'react';
import { usePagination } from '../hooks/usePagination';
import { Pagination } from '../components/ui/Pagination';
import { Link } from 'react-router-dom';
import {
  useTransactions,
  useUpdateTransaction,
} from '../hooks/useTransactions';
import { useCategories, useCategoryTree } from '../hooks/useCategories';
import { useAddCorrection } from '../hooks/useCorrections';
import { useInstallmentGroups, type InstallmentSummary } from '../hooks';
import { CategorySelector } from '../components/ui/CategorySelector';
import { ExcludedFromBudgetBadge } from '../components/ui/ExcludedFromBudgetBadge';
import { ExcludeFromBudgetCheckbox } from '../components/ui/ExcludeFromBudgetCheckbox';
import { useToast } from '../components/ui/ToastContext';
import { useFilterStore } from '../store';
import { useUrlFiltersSync } from '../hooks/useUrlFiltersSync';
import { flattenCategoryTree } from '../services';
import {
  formatAmount,
  formatDateShort,
  formatMonthLabel,
} from '../utils/format';
import { CARD_TYPE_LABELS } from '../utils/constants';
import type { Transaction } from '../types';

function getAvailableMonths(transactions: Transaction[]): string[] {
  const months = new Set<string>();
  transactions.forEach((t) => {
    months.add(t.date.slice(0, 7));
  });
  return Array.from(months).sort().reverse();
}

interface InstallmentBadgeProps {
  transaction: Transaction;
  installmentGroup: InstallmentSummary | undefined;
}

function InstallmentBadge({
  transaction,
  installmentGroup,
}: InstallmentBadgeProps) {
  if (!transaction.isInstallment || !transaction.installmentInfo) {
    return null;
  }

  const { current, total } = transaction.installmentInfo;
  const originalAmount =
    installmentGroup?.originalAmount ?? transaction.amount * total;

  return (
    <div className="mt-1 flex items-center gap-2 text-xs">
      <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-100 text-blue-700">
        Pago {current} de {total}
      </span>
      <span className="text-mid-gray">
        Total: {formatAmount(originalAmount)}
      </span>
    </div>
  );
}

export function TransactionsPage() {
  useUrlFiltersSync();
  const allTransactions = useTransactions();
  const categories = useCategories();
  const categoryTree = useCategoryTree();
  const flatCategories = useMemo(
    () => flattenCategoryTree(categoryTree),
    [categoryTree]
  );
  const installmentGroups = useInstallmentGroups();
  const updateTransaction = useUpdateTransaction();
  const addCorrection = useAddCorrection();
  const { showToast } = useToast();

  const availableMonths = useMemo(
    () => getAvailableMonths(allTransactions),
    [allTransactions]
  );

  const {
    selectedMonth,
    setSelectedMonth,
    selectedCategoryId,
    setSelectedCategoryId,
    searchQuery,
    setSearchQuery,
  } = useFilterStore();

  const filteredTransactions = useTransactions({
    month: selectedMonth || undefined,
    categoryId: selectedCategoryId || undefined,
    search: searchQuery || undefined,
  });

  const sortedTransactions = useMemo(
    () =>
      [...filteredTransactions].sort((a, b) => b.date.localeCompare(a.date)),
    [filteredTransactions]
  );

  const PAGE_SIZE = 25;
  const {
    paginatedData: paginatedTransactions,
    currentPage,
    totalPages,
    goToPrevPage,
    goToNextPage,
  } = usePagination({
    data: sortedTransactions,
    pageSize: PAGE_SIZE,
    resetOn: [selectedMonth, selectedCategoryId, searchQuery],
  });

  const installmentGroupMap = useMemo(() => {
    const map = new Map<string, InstallmentSummary>();
    installmentGroups.forEach((group) => map.set(group.groupId, group));
    return map;
  }, [installmentGroups]);

  const handleCategoryChange = useCallback(
    (transaction: Transaction, newCategoryId: string) => {
      if (transaction.categoryId === newCategoryId) return;

      updateTransaction(transaction.id, { categoryId: newCategoryId });
      addCorrection(transaction, newCategoryId);
      showToast('Categoría actualizada.', 'success');
    },
    [updateTransaction, addCorrection, showToast]
  );

  const handleExcludeChange = useCallback(
    (transaction: Transaction, excluded: boolean) => {
      updateTransaction(transaction.id, { excludedFromBudget: excluded });
      showToast(
        excluded
          ? 'Transacción excluida del presupuesto.'
          : 'Transacción incluida en el presupuesto.',
        'success'
      );
    },
    [updateTransaction, showToast]
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-near-black">
          Transacciones
        </h1>
        <Link
          to="/importar"
          className="px-4 py-2 bg-brand-primary text-white rounded hover:opacity-90 transition min-h-[44px] flex items-center justify-center sm:w-auto"
        >
          Importar transacciones
        </Link>
      </div>

      <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 mb-6">
        <div className="w-full sm:w-auto">
          <label htmlFor="month-filter" className="sr-only">
            Filtrar por mes
          </label>
          <select
            id="month-filter"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="w-full sm:w-auto px-3 py-3 sm:py-2 min-h-[44px] border border-mid-gray rounded bg-white text-near-black"
          >
            <option value="">Todos los meses</option>
            {availableMonths.map((month) => (
              <option key={month} value={month}>
                {formatMonthLabel(month)}
              </option>
            ))}
          </select>
        </div>

        <div className="w-full sm:w-auto">
          <label htmlFor="category-filter" className="sr-only">
            Filtrar por categoría
          </label>
          <select
            id="category-filter"
            value={selectedCategoryId}
            onChange={(e) => setSelectedCategoryId(e.target.value)}
            className="w-full sm:w-auto px-3 py-3 sm:py-2 min-h-[44px] border border-mid-gray rounded bg-white text-near-black"
          >
            <option value="">Todas las categorías</option>
            {flatCategories.map(({ category, depth }) => (
              <option key={category.id} value={category.id}>
                {'\u00A0'.repeat(depth * 4)}
                {depth > 0 ? '└ ' : ''}
                {category.icon} {category.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex-1 min-w-0 sm:min-w-[200px]">
          <label htmlFor="search-filter" className="sr-only">
            Buscar transacciones
          </label>
          <input
            id="search-filter"
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar..."
            className="w-full px-3 py-3 sm:py-2 min-h-[44px] border border-mid-gray rounded bg-white text-near-black"
          />
        </div>
      </div>

      {sortedTransactions.length === 0 ? (
        <div className="text-center py-12 text-mid-gray">
          <p className="text-lg">No hay transacciones</p>
          <p className="text-sm mt-2">
            {allTransactions.length === 0
              ? 'Importa un archivo XLSX para comenzar'
              : 'No se encontraron transacciones con los filtros seleccionados'}
          </p>
        </div>
      ) : (
        <>
          <div className="hidden sm:block overflow-x-auto bg-white rounded-lg shadow">
            <table className="w-full">
              <thead className="bg-light-gray border-b border-mid-gray">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-near-black">
                    Fecha
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-near-black">
                    Descripción
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-near-black">
                    Categoría
                  </th>
                  <th className="text-right px-4 py-3 font-semibold text-near-black">
                    Monto
                  </th>
                  <th className="text-center px-4 py-3 font-semibold text-near-black">
                    Presupuesto
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedTransactions.map((transaction) => (
                  <tr
                    key={transaction.id}
                    className="border-b border-light-gray hover:bg-near-white"
                  >
                    <td className="px-4 py-3 text-mid-gray whitespace-nowrap">
                      {formatDateShort(transaction.date)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-near-black">
                          {transaction.description}
                        </span>
                        <ExcludedFromBudgetBadge
                          excluded={transaction.excludedFromBudget ?? false}
                        />
                      </div>
                      <div className="text-xs text-mid-gray mt-1">
                        {CARD_TYPE_LABELS[transaction.cardType]} •{' '}
                        {transaction.cardIdentifier}
                      </div>
                      <InstallmentBadge
                        transaction={transaction}
                        installmentGroup={
                          transaction.installmentInfo?.groupId
                            ? installmentGroupMap.get(
                                transaction.installmentInfo.groupId
                              )
                            : undefined
                        }
                      />
                    </td>
                    <td className="px-4 py-3">
                      <CategorySelector
                        selectedCategoryId={transaction.categoryId}
                        onSelect={(categoryId) =>
                          handleCategoryChange(transaction, categoryId)
                        }
                        categories={categories}
                      />
                    </td>
                    <td
                      className={`px-4 py-3 text-right font-medium whitespace-nowrap ${
                        transaction.amount < 0
                          ? 'text-green-600'
                          : 'text-near-black'
                      }`}
                    >
                      {transaction.amount < 0 && '+ '}
                      {formatAmount(transaction.amount)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <ExcludeFromBudgetCheckbox
                        excluded={transaction.excludedFromBudget ?? false}
                        onChange={(excluded) =>
                          handleExcludeChange(transaction, excluded)
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="sm:hidden flex flex-col gap-3">
            {paginatedTransactions.map((transaction) => {
              const category = categories.find(
                (c) => c.id === transaction.categoryId
              );
              return (
                <div
                  key={transaction.id}
                  data-testid="transaction-card"
                  className="bg-white rounded-lg shadow p-4"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="text-sm text-mid-gray">
                      {formatDateShort(transaction.date)}
                    </div>
                    <div
                      className={`font-semibold ${
                        transaction.amount < 0
                          ? 'text-green-600'
                          : 'text-near-black'
                      }`}
                    >
                      {transaction.amount < 0 && '+ '}
                      {formatAmount(transaction.amount)}
                    </div>
                  </div>
                  <div className="font-medium text-near-black mb-1 break-words">
                    {transaction.description}
                  </div>
                  {transaction.excludedFromBudget && (
                    <div className="mb-2">
                      <ExcludedFromBudgetBadge
                        excluded={transaction.excludedFromBudget}
                      />
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-xs text-mid-gray mb-2">
                    {category && (
                      <>
                        <span>{category.icon}</span>
                        <span>{category.name}</span>
                        <span>•</span>
                      </>
                    )}
                    <span>{CARD_TYPE_LABELS[transaction.cardType]}</span>
                  </div>
                  <InstallmentBadge
                    transaction={transaction}
                    installmentGroup={
                      transaction.installmentInfo?.groupId
                        ? installmentGroupMap.get(
                            transaction.installmentInfo.groupId
                          )
                        : undefined
                    }
                  />
                  <div className="mt-3 pt-3 border-t border-light-gray flex flex-col gap-3">
                    <CategorySelector
                      selectedCategoryId={transaction.categoryId}
                      onSelect={(categoryId) =>
                        handleCategoryChange(transaction, categoryId)
                      }
                      categories={categories}
                    />
                    <ExcludeFromBudgetCheckbox
                      excluded={transaction.excludedFromBudget ?? false}
                      onChange={(excluded) =>
                        handleExcludeChange(transaction, excluded)
                      }
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPrevPage={goToPrevPage}
            onNextPage={goToNextPage}
          />

          <div className="mt-4 text-sm text-mid-gray text-center">
            Mostrando {paginatedTransactions.length} de{' '}
            {sortedTransactions.length} transacciones
            {sortedTransactions.length !== allTransactions.length &&
              ` (${allTransactions.length} total)`}
          </div>
        </>
      )}
    </div>
  );
}
